import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Swords, ArrowRight, ShieldCheck, Lock } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { DLLoading, EmptyState, StatusPill, Slogan, PlayBackdrop, DLButton } from "@/components/dl/ui";

function CaseCard({ c, entitled }) {
  return (
    <Link to={`/defense-league/cases/${c.dl_case_id}`} data-testid={`dl-case-card-${c.dl_case_id}`}
      className="group relative dl-panel dl-clip rounded-lg overflow-hidden hover:border-gold/50 transition-colors">
      <div className="relative h-28 dl-chalk overflow-hidden border-b border-white/10">
        <PlayBackdrop />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {c.number && <span className="font-display font-bold text-gold text-lg">CASE {c.number}</span>}
          <StatusPill status={c.status} />
        </div>
        <div className="absolute bottom-3 right-3">
          {entitled ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-300"><ShieldCheck className="w-3.5 h-3.5" /> ENTITLED</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono text-white/40"><Lock className="w-3.5 h-3.5" /> CASE PASS</span>
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-display font-bold text-lg text-white group-hover:text-gold transition-colors">{c.title}</h3>
        {c.description && <p className="mt-1 text-sm text-white/50 line-clamp-2">{c.description}</p>}
        <div className="mt-4 inline-flex items-center gap-1 text-gold text-sm font-semibold label-mono">
          Enter Case HQ <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

export default function DLHome() {
  const [cases, setCases] = useState(null);
  const [entIds, setEntIds] = useState(new Set());
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const st = await api.get("/dl/status");
        setEnabled(!!st.data.enabled);
        if (!st.data.enabled) { setLoading(false); return; }
        const [cs, ents] = await Promise.all([
          api.get("/dl/cases"),
          api.get("/dl/entitlements/me").catch(() => ({ data: [] })),
        ]);
        setCases(cs.data || []);
        setEntIds(new Set((ents.data || []).filter((e) => e.effective_status === "active").map((e) => e.dl_case_id)));
      } catch { setCases([]); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-charcoal text-white flex flex-col">
      <Navbar />
      {/* Hero */}
      <section className="relative overflow-hidden dl-chalk-strong border-b border-white/10">
        <PlayBackdrop />
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="inline-flex items-center gap-2 text-gold mb-4">
            <Swords className="w-5 h-5" /> <Slogan>HSTC Defense League</Slogan>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tight leading-[1.05]" data-testid="dl-home-title">
            GET IN<br /><span className="text-gold">THE GAME.</span>
          </h1>
          <p className="mt-5 text-lg text-white/70 max-w-xl">
            Everybody has an opinion about a criminal case. What would you actually <em className="text-white not-italic font-semibold">do</em> if you had to defend it?
          </p>
          <p className="mt-2 text-white/50 max-w-xl">Think you could build a better defense? Build one.</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#seasons"><DLButton data-testid="dl-home-cta"><Swords className="w-4 h-4" /> Build Your Team. Build Your Case.</DLButton></a>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-[11px] font-mono uppercase tracking-wider text-white/40">
            <span>If it's not in the Case Record, it's not in the game.</span>
            <span className="text-white/20">•</span>
            <span>If you didn't lock the play, you didn't call it.</span>
          </div>
        </div>
      </section>

      {/* Case seasons */}
      <section id="seasons" className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-12">
        <Slogan className="block mb-2">Case Seasons</Slogan>
        <h2 className="font-display font-bold text-2xl sm:text-3xl mb-6">Choose your case.</h2>
        {loading ? <DLLoading label="Loading case seasons…" /> :
          !enabled ? (
            <EmptyState icon={Lock} title="Defense League is not open yet" desc="The league is currently closed. Check back soon." testid="dl-disabled" />
          ) : (cases && cases.length > 0) ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {cases.map((c) => <CaseCard key={c.dl_case_id} c={c} entitled={entIds.has(c.dl_case_id)} />)}
            </div>
          ) : (
            <EmptyState icon={Swords} title="No case seasons are open yet" desc="New Defense League cases are on the way. When a case opens, you'll build a team, study the record, and call your plays here." testid="dl-no-cases" />
          )}
      </section>
      <Footer />
    </div>
  );
}
